'use client'

import { memo, useCallback, useEffect, useState, type FC } from 'react'
import { useWatch } from 'react-hook-form'

import { FieldHelpPopover, FieldShell, type FieldRendererProps } from './FieldShell'
import {
  AMOUNT_PARAM_KEY,
  buildRow,
  getAmountField,
  getAvailableBalance,
  getCurrency,
  getEditableFields,
  isRowOverMax,
  parseAmount,
  sumCheckedRows,
  toEntries,
  type ChecklistField,
  type ChecklistOption,
  type ChecklistOptionField,
  type ChecklistRow,
} from '../lib/checklistHelper'
import { CheckboxBlock, cn, Input, Label, MarqueeText } from '@fp/ui-kit'
import { formatCurrency } from '@lib/formatCurrency'

const EMPTY_TEXT = 'No options available.'
const AMOUNT_STEP = 0.01

type AmountInputProps = {
  label: string
  value: string
  disabled?: boolean
  invalid?: boolean
  onChange: (value: string) => void
}

const AmountInput: FC<AmountInputProps> = memo(({ label, value, disabled, invalid, onChange }) => (
  <Input
    type='number'
    step={AMOUNT_STEP}
    min={0}
    aria-label={label}
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value || '0')}
    className={cn(
      'h-9 w-28 shrink-0 text-right tabular-nums',
      invalid && 'border-destructive-foreground focus-visible:ring-destructive-foreground',
    )}
  />
))
AmountInput.displayName = 'AmountInput'

type ChecklistRowProps = {
  option: ChecklistOption
  row: ChecklistRow
  rowId: string
  index: number
  onToggle: (index: number, checked: boolean) => void
  onValueChange: (index: number, paramKey: string, value: string) => void
}

const ChecklistRowItem: FC<ChecklistRowProps> = memo(({ option, row, rowId, index, onToggle, onValueChange }) => {
  const amountField = getAmountField(option)
  const otherFields = getEditableFields(option).filter((f) => f.param_key !== amountField?.param_key)

  return (
    <div className='grid gap-2 px-3 py-2'>
      <div className='flex items-center gap-3'>
        <CheckboxBlock id={`${rowId}-checkbox`} checked={row.checked} onCheckedChange={(c) => onToggle(index, !!c)} />

        <Label htmlFor={`${rowId}-checkbox`} className='min-w-0 flex-1 cursor-pointer'>
          <MarqueeText>{option.name}</MarqueeText>
        </Label>

        <FieldHelpPopover fieldName={option.name} helperText={option.description} />

        {amountField && (
          <AmountInput
            label={amountField.name || 'Amount'}
            value={row.values[amountField.param_key] ?? ''}
            disabled={!row.checked}
            invalid={isRowOverMax(option, row)}
            onChange={(value) => onValueChange(index, amountField.param_key, value)}
          />
        )}
      </div>

      {otherFields.map((optionField: ChecklistOptionField) => (
        <div key={optionField.param_key} className='grid gap-1 pl-7'>
          <Label htmlFor={`${rowId}-${optionField.param_key}`} className='text-xs text-muted-foreground'>
            {optionField.name || optionField.param_key}
          </Label>
          <Input
            id={`${rowId}-${optionField.param_key}`}
            type={optionField.type === 'number' ? 'number' : 'text'}
            value={row.values[optionField.param_key] ?? ''}
            disabled={!row.checked}
            onChange={(e) => onValueChange(index, optionField.param_key, e.target.value)}
          />
        </div>
      ))}
    </div>
  )
})
ChecklistRowItem.displayName = 'ChecklistRowItem'

type ChecklistTotalsProps = { total: number; balance?: number; currency: string }

const ChecklistTotals: FC<ChecklistTotalsProps> = memo(({ total, balance, currency }) => {
  const isOverBalance = typeof balance !== 'undefined' && total > balance

  return (
    <div className='grid gap-1 border-t border-muted pt-3'>
      <div className='flex items-center justify-between gap-2'>
        <span className='text-sm'>Total</span>
        <span
          className={cn(
            'text-lg font-medium tabular-nums',
            isOverBalance ? 'text-destructive-foreground' : 'text-foreground',
          )}
        >
          {formatCurrency(total, currency)}
        </span>
      </div>
      {typeof balance !== 'undefined' && (
        <div className='flex items-center justify-between gap-2 text-xs text-muted-foreground'>
          <span>Available balance</span>
          <span className='tabular-nums'>{formatCurrency(balance, currency)}</span>
        </div>
      )}
    </div>
  )
})
ChecklistTotals.displayName = 'ChecklistTotals'

export type EditableChecklistFieldProps = Omit<FieldRendererProps, 'paramsField'> & { paramsField: ChecklistField }

export const EditableChecklistField: FC<EditableChecklistFieldProps> = memo(
  ({ paramsField, field, error, helperText }) => {
    const { options } = paramsField
    const { onChange } = field
    const [rows, setRows] = useState<Array<ChecklistRow>>(() => options.map(buildRow))
    const externalAmount = parseAmount(useWatch({ name: AMOUNT_PARAM_KEY }))

    useEffect(() => {
      setRows(options.map(buildRow))
    }, [options])

    const emit = useCallback(
      (next: Array<ChecklistRow>) => {
        setRows(next)
        onChange(toEntries(next))
      },
      [onChange],
    )

    const toggleRow = useCallback(
      (index: number, checked: boolean) => emit(rows.map((row, i) => (i === index ? { ...row, checked } : row))),
      [emit, rows],
    )

    const changeValue = useCallback(
      (index: number, paramKey: string, value: string) =>
        emit(rows.map((row, i) => (i === index ? { ...row, values: { ...row.values, [paramKey]: value } } : row))),
      [emit, rows],
    )

    return (
      <FieldShell paramsField={paramsField} error={error} helperText={helperText}>
        {options.length ? (
          <div className='divide-y divide-muted rounded-md border border-muted'>
            {options.map((option, index) => {
              const row = rows[index]

              if (!row) return null

              const rowId = `dynamic-field-${paramsField.param_key}-${index}`

              return (
                <ChecklistRowItem
                  key={rowId}
                  option={option}
                  row={row}
                  rowId={rowId}
                  index={index}
                  onToggle={toggleRow}
                  onValueChange={changeValue}
                />
              )
            })}
          </div>
        ) : (
          <p className='text-sm text-muted-foreground'>{EMPTY_TEXT}</p>
        )}

        <ChecklistTotals
          total={sumCheckedRows(options, rows) + externalAmount}
          balance={getAvailableBalance(paramsField)}
          currency={getCurrency(paramsField)}
        />
      </FieldShell>
    )
  },
)
EditableChecklistField.displayName = 'EditableChecklistField'
